import { z } from "zod";

/**
 * M23 — what a notification IS, as opposed to which Telegram channel it goes to.
 *
 * `target` has always answered "system or trades", which is a routing question about two
 * private Telegram channels. It cannot answer "is this the kind of thing I want on my phone
 * at 3am", because both channels carry everything from a filled order to a crashed
 * connection. `kind` is that second axis, and it is the prerequisite for every per-notification
 * choice: which ones push, which ones badge, which ones can be muted while a debug session is
 * running and unmuted after.
 *
 * **Text, not a pgEnum.** This list grows every time a formatter is added — 34 of them today —
 * and a pgEnum means a migration per addition and values that can never be removed. The two
 * existing enums (`target`, `status`) have two and three stable values, which is a different
 * situation. Validation happens at the boundary against `notificationKindSchema`, and an
 * unrecognised kind read back from an older row renders as itself rather than throwing.
 *
 * Categories exist for the UI only, and are derived here rather than stored: thirty switches
 * on a phone screen is not "customizable", it is a wall. Nothing keys off a category in the
 * database.
 */

export const NOTIFICATION_CATEGORY = {
  trading: "trading",
  signals: "signals",
  integrity: "integrity",
  system: "system",
  reports: "reports",
} as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORY)[keyof typeof NOTIFICATION_CATEGORY];

export const NOTIFICATION_CATEGORY_LABEL: Record<NotificationCategory, string> = {
  trading: "Trading",
  signals: "Signals",
  integrity: "Channel integrity",
  system: "System",
  reports: "Reports",
};

type KindEntry = {
  category: NotificationCategory;
  /** Shown as the switch's label. Short enough for a phone row. */
  label: string;
  /**
   * Whether this is something being WRONG rather than something happening.
   *
   * Andy's stated end state is "when it's stable I only want to be told when something is
   * wrong", so this flag is what a preset writes against — not a second delivery mechanism
   * sitting beside the per-kind switches, which would give two answers to one question.
   */
  alert: boolean;
};

/**
 * Every kind this system can emit, with the two facts the UI needs about it.
 *
 * Deliberately finer than the switches Andy will actually flip: kinds are grouped by category
 * in the UI and toggled in blocks, but the stored preference is per kind, so a block toggle
 * that later needs an exception does not require a migration.
 */
export const NOTIFICATION_KINDS = {
  // ── trading ────────────────────────────────────────────────────────────────────────
  trade_opened: { category: "trading", label: "Order opened", alert: false },
  manual_trade: { category: "trading", label: "Manual order opened", alert: false },
  level_hit: { category: "trading", label: "Take-profit or stop hit", alert: false },
  signal_closed: { category: "trading", label: "Signal fully closed", alert: false },
  rule_applied: { category: "trading", label: "Management rule fired", alert: false },
  pending_expired: { category: "trading", label: "Pending order expired", alert: false },
  order_in_doubt: { category: "trading", label: "Order state unknown", alert: true },
  order_resolved: { category: "trading", label: "Unknown order resolved", alert: false },
  position_vanished: { category: "trading", label: "Position vanished at the broker", alert: true },
  external_position: { category: "trading", label: "Position we did not open", alert: true },

  // ── signals ────────────────────────────────────────────────────────────────────────
  signal_skipped: { category: "signals", label: "Signal skipped", alert: false },
  signal_dropped: { category: "signals", label: "Extra signal dropped", alert: false },
  signal_parked: { category: "signals", label: "Signal parked", alert: false },
  signal_edited: { category: "signals", label: "Signal edited after we acted", alert: false },
  signal_failed: { category: "signals", label: "Signal failed to execute", alert: true },
  signal_stranded: { category: "signals", label: "Signal stranded", alert: true },
  decision_escalated: { category: "signals", label: "Decision needs you", alert: true },
  decision_timeout: { category: "signals", label: "Decision timed out", alert: true },
  follow_up: { category: "signals", label: "Follow-up message handled", alert: false },

  // ── channel integrity ──────────────────────────────────────────────────────────────
  foul_play: { category: "integrity", label: "Foul play detected", alert: true },

  // ── system ─────────────────────────────────────────────────────────────────────────
  boot: { category: "system", label: "App started", alert: false },
  health: { category: "system", label: "Health changed", alert: true },
  error: { category: "system", label: "Error", alert: true },
  budget: { category: "system", label: "Budget exhausted", alert: true },
  reconcile_repair: { category: "system", label: "Reconciler repaired something", alert: true },
  action_refused: { category: "system", label: "App action refused", alert: false },
  account: { category: "system", label: "Account changed", alert: false },
  outbox: { category: "system", label: "Notification backlog", alert: true },
  test: { category: "system", label: "Test message", alert: false },

  // ── reports ────────────────────────────────────────────────────────────────────────
  report: { category: "reports", label: "Periodic report", alert: false },
  briefing: { category: "reports", label: "Daily briefing", alert: false },
  channel_verdict: { category: "reports", label: "Monthly channel verdict", alert: false },
} as const satisfies Record<string, KindEntry>;

export type NotificationKind = keyof typeof NOTIFICATION_KINDS;

export const NOTIFICATION_KIND_LIST = Object.keys(NOTIFICATION_KINDS) as NotificationKind[];

export const notificationKindSchema = z.enum(
  NOTIFICATION_KIND_LIST as [NotificationKind, ...NotificationKind[]],
);

export function isKnownNotificationKind(value: string): value is NotificationKind {
  return Object.hasOwn(NOTIFICATION_KINDS, value);
}

/**
 * A stored kind's display label, falling back to the raw string.
 *
 * A row written by an older build carries a kind this build may not know. Rendering it as
 * itself is the honest outcome; throwing would make one unrecognised row take down the whole
 * notifications page, and hiding it would make the page quietly incomplete.
 */
export function notificationKindLabel(kind: string): string {
  return isKnownNotificationKind(kind) ? NOTIFICATION_KINDS[kind].label : kind;
}

export function notificationKindCategory(kind: string): NotificationCategory | null {
  return isKnownNotificationKind(kind) ? NOTIFICATION_KINDS[kind].category : null;
}

export function isAlertKind(kind: string): boolean {
  return isKnownNotificationKind(kind) ? NOTIFICATION_KINDS[kind].alert : false;
}

/**
 * M23c — where a kind is allowed to go.
 *
 * Two arms, decided together and stored together even though push does not exist until
 * M23d. The stored value is zod-parsed on read and a shape change would make every stored
 * preference fail validation and silently revert to the defaults — so the shape is settled
 * once, here, rather than migrated between two commits of the same milestone.
 *
 * **Partial by design.** An absent kind means "whatever the default says", so adding a
 * formatter never needs a backfill and the stored object stays as small as the number of
 * decisions Andy has actually made.
 */
export const deliveryChannelsSchema = z.object({
  /** The private Telegram channels. On by default for everything: this is today's behaviour. */
  telegram: z.boolean(),
  /** Web push to a subscribed device. Inert until a device subscribes (M23d). */
  push: z.boolean(),
});
export type DeliveryChannels = z.infer<typeof deliveryChannelsSchema>;

export const notificationDeliverySchema = z.record(z.string(), deliveryChannelsSchema);
export type NotificationDelivery = z.infer<typeof notificationDeliverySchema>;

/**
 * What a kind does when nobody has said otherwise.
 *
 * Telegram on, always — the defaults have to reproduce today's behaviour exactly, or
 * shipping this milestone would itself be the act that silences something.
 *
 * Push follows the `alert` flag, which makes the moment a device first subscribes
 * implement Andy's stated end state ("when it's stable I only want to be told when
 * something is wrong") with no configuration at all. It costs nothing to be wrong about
 * before then: with no subscription there is nothing to push to.
 *
 * An unknown kind — a row from a newer build, or `legacy` — gets telegram on and push off.
 * Erring toward the arm that has always carried everything.
 */
export function defaultDeliveryFor(kind: string): DeliveryChannels {
  return { telegram: true, push: isAlertKind(kind) };
}

export function deliveryFor(prefs: NotificationDelivery, kind: string): DeliveryChannels {
  return prefs[kind] ?? defaultDeliveryFor(kind);
}

/** The "only tell me when something is wrong" preset, as a stored value. */
export function alertsOnlyDelivery(): NotificationDelivery {
  return Object.fromEntries(
    NOTIFICATION_KIND_LIST.map((kind) => [kind, { telegram: isAlertKind(kind), push: isAlertKind(kind) }]),
  );
}
